<template>
  <div :class="classes()">
    <component :is="!isAuthenticated ? 'Container' : 'div'">
      <Headline size="m" isViewHeadline>Pflegegradanalyse</Headline>

      <IntroBanner
        :headline="$t('careLevelCalculator.analysis.infoBanner.headline')"
        :copy="$t('careLevelCalculator.analysis.infoBanner.copy')"
        :bottomSpace="40"
        :localStorage="LS_CARE_LEVEL_ANALYSIS_INFO_BANNER"
      />

      <Widget
        :headline="$t('careLevelCalculator.analysis.result.headline')"
        :bottom="{
          href: ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
          label: $t(
            `careLevelCalculator.analysis.careLevelBar.cta${wizardIsFinished ? '.repeat' : ''}`
          ),
          onClick: trackCareCalcBtn,
        }"
        :bottomIsButton="resultVariant === 'default'"
      >
        <InViewAnimation>
          <Headline slot="1" size="s" responsive bordered>
            {{ $t(`careLevelCalculator.analysis.result.subline.${resultVariant}`) }}
          </Headline>
          <template slot="2">
            <CareLevelBar
              v-if="resultVariant === 'default'"
              :class="`${el}__careLevelBar`"
              :headline="$t('careLevelCalculator.analysis.careLevelBar.headline.default')"
              centeredCareLevel
            />
            <CareLevelBar
              v-if="resultVariant === '1'"
              :class="`${el}__careLevelBar`"
              :headline="$t('careLevelCalculator.analysis.careLevelBar.headline')"
              :subline="
                $t('careLevelCalculator.analysis.careLevelBar.subline', {
                  date: formatDate(calculatedCareLevelAt),
                })
              "
              :careLevel="calculatedCareLevel"
              careLevelColor="primaryCoral"
              centeredCareLevel
            />
            <CareLevelBar
              v-if="resultVariant === '2'"
              :class="`${el}__careLevelBar`"
              :headline="$t('careLevelCalculator.analysis.careLevelBar.headline')"
              :subline="
                $t('careLevelCalculator.analysis.careLevelBar.subline', {
                  date: formatDate(calculatedCareLevelAt),
                })
              "
              careLevelColor="primaryCoral"
              centeredCareLevel
            />
            <div
              v-if="resultVariant === '3' || resultVariant === '4'"
              :class="`${el}__careLevelBars ${el}__item`"
            >
              <CareLevelBar
                :headline="$t('careLevelCalculator.analysis.careLevelBar.headline')"
                :subline="
                  $t('careLevelCalculator.analysis.careLevelBar.subline', {
                    date: formatDate(calculatedCareLevelAt),
                  })
                "
                :careLevel="calculatedCareLevel"
                careLevelColor="primaryCoral"
              />
              <CareLevelBar
                :headline="$t('careLevelCalculator.analysis.careLevelBar.headline.mdk')"
                :subline="
                  careLevelSituation === 1
                    ? careLevelDate
                      ? $t('careLevelCalculator.analysis.careLevelBar.subline.applied', {
                          date: formatDate(calculatedCareLevelAt),
                        })
                      : $t('careLevelCalculator.analysis.careLevelBar.subline.applied.noDate')
                    : careLevelDate
                    ? $t('careLevelCalculator.analysis.careLevelBar.subline.mdk', {
                        date: formatDate(careLevelDate),
                      })
                    : $t('careLevelCalculator.analysis.careLevelBar.subline.mdk.noDate')
                "
                :careLevel="careLevel"
                careLevelColor="secondaryMintLight"
              />
            </div>
          </template>

          <div v-if="resultVariant !== 'default'" slot="3" :class="`${el}__notice ${el}__item`">
            <Copy :staticClass="`${el}__copy`" color="tertiaryLight" bold>
              <Icon name="error-warning-line" size="l" color="tertiaryLight" />
              {{ $t('careLevelCalculator.analysis.careLevelBar.notice1') }}
            </Copy>
            <Copy color="tertiaryLight">
              {{ $t('careLevelCalculator.analysis.careLevelBar.notice2') }}
            </Copy>
          </div>
        </InViewAnimation>
      </Widget>

      <template v-if="resultVariant !== 'default'">
        <Widget :headline="$t('careLevelCalculator.analysis.download.headline')">
          <InViewAnimation :delay="3">
            <ActionBar
              slot="1"
              :headline="$t('careLevelCalculator.analysis.download.actionBar.headline')"
              :subline="$t('careLevelCalculator.analysis.download.actionBar.subline')"
              iconName="file-download-line"
              :button="{
                loading: loadPdf,
                label: $t('careLevelCalculator.analysis.download.actionBar.button'),
              }"
              @click="handleDownload"
            />
          </InViewAnimation>
        </Widget>

        <InViewAnimation v-if="isAuthenticated" :delay="4">
          <div slot="1" :class="`${el}__nextStep`">
            <Icon name="survey-line" :class="`${el}__nextStepIcon`" color="primaryBlue" />
            <Copy element="div" size="xl" color="primaryBlue" bold>
              {{ $t('careLevelCalculator.analysis.download.nextStep.healine') }}
            </Copy>
            <Link
              :class="`${el}__nextStepLink`"
              icon="arrow-down-line"
              iconPos="right"
              @click="scrollToNextStep"
              noUnderline
            >
              {{ $t('careLevelCalculator.analysis.download.nextStep.link') }}
            </Link>
          </div>
        </InViewAnimation>

        <Headline :staticClass="`${el}__subheading`" size="s" responsive isSubheading>
          {{ $t('careLevelCalculator.analysis.subheading1') }}
        </Headline>

        <Widget :headline="$t('careLevelCalculator.analysis.pflegegradeinstufung.headline')">
          <CareLevelResultTable />
        </Widget>

        <Widget :headline="$t('careLevelCalculator.analysis.beeintraechtigungen.headline')">
          <CareLevelModuleStatistic @onTrackingClick="trackClick" />
        </Widget>

        <Headline
          v-if="calculatedCareLevel"
          :staticClass="`${el}__subheading`"
          size="s"
          responsive
          isSubheading
        >
          {{ $t('careLevelCalculator.analysis.subheading2') }}
        </Headline>

        <Widget
          v-if="calculatedCareLevel"
          :headline="$t('careLevelCalculator.analysis.careService.headline')"
        >
          <div :class="`${el}__careService`">
            <div class="u-textAlign--center">
              <Copy
                element="span"
                :staticClass="`${el}__careServiceTag`"
                size="xl"
                color="neutral0"
                bold
              >
                {{
                  $t('careLevelCalculator.analysis.careService.tag', {
                    value: calculatedCareLevel,
                  })
                }}
              </Copy>
            </div>

            <Headline size="s" responsive centered>
              {{
                $t('careLevelCalculator.analysis.careService.amount', {
                  value: formatNumber(totalAmountPerYear(), 0),
                })
              }}
            </Headline>

            <Copy color="tertiaryLight" centered>
              {{ $t('careLevelCalculator.analysis.careService.legal') }}
            </Copy>
          </div>
        </Widget>

        <template v-if="isAuthenticated">
          <Headline
            :id="scrollToId"
            :staticClass="`${el}__subheading`"
            size="s"
            responsive
            isSubheading
          >
            {{ $t('careLevelCalculator.analysis.subheading3') }}
          </Headline>

          <TaskSchedulerWidget @onTrackingClick="trackClick" />
        </template>
      </template>
    </component>
  </div>
</template>

<script>
import Vue from 'vue';
import Headline from '@/objects/Headline';
import Copy from '@/objects/Copy';
import Icon from '@/objects/Icon';
import Link from '@/objects/Link';
import Widget from '@/objects/Widget';
import Container from '@/objects/Container';
import CareLevelBar from '@/components/CareLevelBar';
import ActionBar from '@/components/ActionBar';
import IntroBanner from '@/components/IntroBanner';
import CareLevelResultTable from '@/components/CareLevelResultTable';
import CareLevelModuleStatistic from '@/components/CareLevelModuleStatistic';
import InViewAnimation from '@/components/InViewAnimation';
import careLevelMixin from '@/mixins/careLevel';

import { TaskSchedulerWidget } from '@/views/TaskScheduler';

import { formatNumber, formatDate } from '@/helpers/formatter';

import { mapFieldsAuth, mapFieldsHealth, mapFieldsTask } from '@/store/mapFields';

import { scrollTo } from '@/helpers/scroll';

import {
  LS_CARE_LEVEL_ANALYSIS_INFO_BANNER,
  LS_CARE_LEVEL_CALCULATOR_JWT,
} from '@/constants/storage';

import {
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR,
} from '@/constants/routes';
import {
  ROUTE_API_CLEAR_TASK_SCHEDULER,
  ROUTE_API_UNREGISTERED_USER_DOI,
  ROUTE_API_GET_CARE_LEVEL_DATA_FROM_UUID,
  ROUTE_API_GET_CARE_LEVEL_WIZARD_PDF,
  ROUTE_API_GET_CARE_LEVEL_WIZARD_PDF_UNREGISTERED,
} from '@/constants/apiRoutes';

import { TASKS_UPDATE_CURRENT_TASK } from '@/store/actions/tasks';
import { CARELEVEL_REQUEST } from '@/store/actions/careLevelCalculator';

import careServiceFacts, { serviceInfos } from '@/content/services/CareService/CareServiceFacts';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';
import { HEALTH_SET_CARE_LEVEL } from '@/store/actions/health';
import { trackBIUnregisteredUser } from '@/helpers/biTracking/biTracking';
import Tracking from '@/helpers/tracking/Tracking';

export default {
  name: 'CareLevelCalculatorAnalysis',
  props: {},
  mixins: [careLevelMixin],
  components: {
    Headline,
    Copy,
    Icon,
    Link,
    Widget,
    Container,
    CareLevelBar,
    ActionBar,
    CareLevelResultTable,
    CareLevelModuleStatistic,
    InViewAnimation,
    TaskSchedulerWidget,
    IntroBanner,
  },
  computed: {
    ...mapFieldsAuth(['isAuthenticated']),
    ...mapFieldsHealth([
      'careLevel',
      'careLevelDate',
      'healthUuid',
      'unregisteredUuid',
      'careLevelSituation',
    ]),
    ...mapFieldsTask(['completedTasks']),

    resultVariant() {
      // default -> Es liegt noch keine Analyse vor.
      // 1 -> Sie haben vermutlich einen Pflegegrad und keinen tatsächlichen Pflegegrad
      // 2 -> Sie haben vermutlich keinen Pflegegrad
      // 3 -> Sie haben vermutlich einen höheren Pflegegrad.
      // 4 -> Sie haben vermutlich keinen höheren Pflegegrad.

      let variant = 'default';

      if ((!this.isAuthenticated && this.token) || this.isAuthenticated) {
        if (this.calculatedCareLevel && !this.careLevel) {
          variant = '1';
        }
        if (this.calculatedCareLevel === 0) {
          variant = '2';
        }

        if (
          this.calculatedCareLevel &&
          this.careLevel &&
          this.calculatedCareLevel > this.careLevel
        ) {
          variant = '3';
        }

        if (
          this.calculatedCareLevel &&
          this.calculatedCareLevel >= 0 &&
          this.careLevel &&
          this.calculatedCareLevel <= this.careLevel
        ) {
          variant = '4';
        }
      }

      return variant;
    },
  },

  beforeRouteLeave(to, from, next) {
    setInterval(() => {
      next();
    }, 10);
  },

  data() {
    return {
      formatDate,
      LS_CARE_LEVEL_ANALYSIS_INFO_BANNER,
      prevRoute: null,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
      scrollToId: 'taskSchedulerWidget',
      formatNumber,
      pflegegrad: null,
      token: null,
      loadPdf: false,
      el: 'v-careLevelCalculatorAnalysis',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },
  created() {
    if (!this.isAuthenticated) {
      const { token } = this.$route.params;
      this.token = token;

      if (token) {
        Vue.axios
          .post(ROUTE_API_GET_CARE_LEVEL_DATA_FROM_UUID, { jwt: token })
          .then((res) => {
            const {
              module_points_json,
              care_level,
              calculated_care_level,
              calculated_care_level_at,
            } = res.data.careLevelWizard[0];

            const careLevel = typeof care_level === 'string' ? parseInt(care_level) : care_level;
            const calculatedCareLevel =
              typeof calculated_care_level === 'string'
                ? parseInt(calculated_care_level)
                : calculated_care_level;

            this.$store.dispatch(HEALTH_SET_CARE_LEVEL, {
              careLevel,
              unregisteredUuid: res.data.uuid,
              careLevelSituation: res.data.careLevelWizard['careLevelSituation'],
            });
            this.$store.dispatch(CARELEVEL_REQUEST, {
              modulePoints: module_points_json ? JSON.parse(module_points_json) : null,
              calculatedCareLevelAt: calculated_care_level_at,
              calculatedCareLevel,
            });
            Vue.axios
              .post(ROUTE_API_UNREGISTERED_USER_DOI, { token: res.data.newsletterToken })
              .then((response) => {
                if (!response.data.wasCalledBefore) {
                  this.trackGaDoi();
                }
              });
            trackBIUnregisteredUser('pgr_newsletter_doi');
            localStorage.setItem(LS_CARE_LEVEL_CALCULATOR_JWT, token);
          })
          .catch(() => {
            this.$router.push(ROUTE_SERVICE_CARELEVEL_CALCULATOR);
          });
      } else {
        this.$router.push(ROUTE_SERVICE_CARELEVEL_CALCULATOR);
      }
    }
  },
  mounted() {
    /**
     * Die Tasks dürfen neu gesetzt werden, wenn der User aus dem Wizard kommt und die Bedingen
     * im Kommentar von setTasks() erfüllt
     */
    const isPreviousRouteThePflegegradWizard = this.prevRoute?.path.includes(
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD
    );

    if (this.isAuthenticated && isPreviousRouteThePflegegradWizard) {
      this.setTasks();
    }
  },
  methods: {
    setTasks() {
      /**
       *
       * Es gibt ja vier Ergebnis-Typen beim PGR:
       * 1. "Sie haben vermutlich einen Pflegegrad." -> Aktueller Task wäre nun: P2-20 (Antrag stellen)
       * 2. "Sie haben vermutlich keinen Pflegegrad." -> Aktueller Task wäre nun: P1-70 (Leistungen ohne Pflegegrad)
       * 3. "Sie haben vermutlich einen höheren Pflegegrad." -> Aktueller Task wäre nun: P2-20 (Antrag stellen)
       * 4. "Sie haben vermutlich keinen höheren Pflegegrad." -> Aktuelle Aufgabe würde nicht aktualisiert werden. Alles bleibt wie es ist.
       *
       * Im grunde könnte man ja pauschal sagen, wenn 1,2 oder 3 zutrifft, könnte der Aufgabenplaner zurückgesetzt werden und startet mit der aktuellen Aufgabe.
       *
       * this.resultVariant gibt die Ergebnis-Typen zurück von 1 bis 4
       */

      if (this.resultVariant === '1' || this.resultVariant === '2' || this.resultVariant === '3') {
        const taskId = this.resultVariant === '2' ? 'P1-70' : 'P2-20';

        if (this.resultVariant === '1') {
          this.$store.dispatch(TASKS_UPDATE_CURRENT_TASK, taskId);
        } else {
          Vue.axios
            .post(ROUTE_API_CLEAR_TASK_SCHEDULER, { uuid: this.healthUuid })
            .then(() => this.$store.dispatch(TASKS_UPDATE_CURRENT_TASK, taskId));
        }
      }
    },

    handleDownload() {
      this.loadPdf = true;
      let urlData = null;

      if (this.isAuthenticated) {
        urlData = {
          url: ROUTE_API_GET_CARE_LEVEL_WIZARD_PDF,
          data: { personInNeedOfCareUuid: this.healthUuid },
        };
      } else {
        urlData = {
          url: ROUTE_API_GET_CARE_LEVEL_WIZARD_PDF_UNREGISTERED,
          data: { uuid: this.unregisteredUuid },
        };
      }

      Vue.axios
        .post(urlData.url, urlData.data, {
          responseType: 'arraybuffer',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/pdf',
          },
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${this.$t('careLevelCalculator.download.filename')}.pdf`);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          this.loadPdf = false;
          this.trackClick(
            'download-' + this.$t('careLevelCalculator.download.filename') + '.pdf',
            'Download'
          );
        })
        .catch(() => (this.loadPdf = false));
    },

    scrollToNextStep() {
      scrollTo(`#${this.scrollToId}`, -80);
      this.trackClick(
        'link-' + this.$t('careLevelCalculator.analysis.download.nextStep.link'),
        'link'
      );
    },

    getCareLevelFact() {
      return careServiceFacts.filter((c) => c.pflegegrad === this.calculatedCareLevel)[0];
    },

    getFact(id) {
      return {
        amount: this.getCareLevelFact()[id],
        payout: this.payoutType(id),
      };
    },

    payoutType(id) {
      const match = serviceInfos.filter((s) => s.label === id)[0];
      return match.payout;
    },

    totalAmountPerYear() {
      let value = 0;
      const facts = careServiceFacts.filter((c) => c.pflegegrad === this.calculatedCareLevel)[0];
      Object.keys(facts).map((c) => {
        if (c !== 'pflegegrad' && c !== 'pflegegeld') {
          value +=
            this.getFact(c).payout === 'monthly'
              ? this.getFact(c).amount * 12
              : this.getFact(c).amount;
        }
      });
      return value;
    },

    trackCareCalcBtn() {
      if (this.calculatedCareLevel !== null) {
        this.trackClick(
          'link-' + this.$t('careLevelCalculator.analysis.careLevelBar.cta.repeat'),
          'link'
        );
      } else if (
        (this.careLevel !== null && this.careLevel !== 0) ||
        (this.calculatedCareLevel !== null && this.calculatedCareLevel > 0)
      ) {
        this.trackClick('link-' + this.$t('careLevelCalculator.analysis.careLevelBar.cta'), 'link');
      } else {
        this.trackClick(
          'btn-' + this.$t('careLevelCalculator.analysis.careLevelBar.cta'),
          'button'
        );
      }
    },

    trackClick(label, element) {
      const product = this.isAuthenticated ? 'Pflegegradanalyse' : 'Pflegegradrechner';

      const reducedResult = this.isAuthenticated ? '' : 'redErgebnis';

      const withCareLevel = this.careLevel !== null && this.careLevel !== 0 ? 'mit-PG' : 'ohne-PG';

      const labelText =
        product + '-' + reducedResult + (this.isAuthenticated ? withCareLevel : '') + '_' + label;

      createTrackingData(product, labelText, element);
    },

    trackGaDoi() {
      Tracking.trackDatalayer({
        event: 'gaEvent',
        event_name: 'conversion',
        conversion: {
          conversion_type: 'subscription',
          conversion_product: 'pflegegradrechner',
        },
      });
    },
  },
};
</script>
